import { type PermissionedResource } from '@lib/permission';
import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type IProject } from './projectEndpoints';

export const shareAccessLevels = ['VIEW', 'INSPECT', 'FULL'] as const;

export type ShareAccessLevel = typeof shareAccessLevels[number];

export interface IFolderShare {
  teamId: string;
  accessLevel: ShareAccessLevel;
  sharedAt?: string;
}

export interface IFolder extends PermissionedResource {
  _id: string;
  name: string;
  path: string;
  parentFolder?: string | IFolder | null;
  /** Set only on team root folders. */
  teamId?: string;
  /** Team that owns this folder, resolved from its root — set on nested folders too. */
  ownerTeamId?: string;
  /** Set on entries surfaced at the virtual root because another team shared them. */
  isSharedWithMe?: boolean;
  children: IFolderChild[];
  sharedWith?: IFolderShare[];
  createdAt: string;
  updatedAt: string;
}

export const folderChildTypes = ['Folder', 'Project'] as const;

export type FolderChildType = typeof folderChildTypes[number];

export interface IFolderChild { type: FolderChildType, item: string | IFolder | IProject }

export function isFolder(child: IFolderChild): child is { type: 'Folder'; item: IFolder } {
  return child.type === 'Folder';
}

export function isProject(child: IFolderChild): child is { type: 'Project'; item: IProject } {
  return child.type === 'Project';
}

export const projectApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFolders: builder.query<IFolder[], void>({
      query: () => '/v1/folders',
      providesTags: (result = []) => [
        ...result.map(({ _id }) => ({ type: 'Folder' as const, id: _id ?? 'root' })),
        { type: 'Folder', id: 'LIST' },
      ],
      onQueryStarted: handleApiResult(),
    }),
    getFolder: builder.query<IFolder, string>({
      query: (id) => `/v1/folders/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Folder', id: id ?? 'root' }],
      onQueryStarted: handleApiResult(),
    }),
    getFolderByPath: builder.query<IFolder, string>({
      query: (path) => `/v1/folders?path=${encodeURIComponent(path)}`,
      providesTags: (result) => {
        const tags: ({ type: 'Folder' | 'Project'; id: string })[] = result
          ? [{ type: 'Folder', id: result._id ?? 'root' }]
          : [];

        if (result?.children?.length) {
          result.children.forEach((child) => {
            if (child.type == 'Folder') {
              const folderChildId = typeof child.item === 'string' ? child.item : (child.item as IFolder)._id;

              if (folderChildId) tags.push({ type: 'Folder', id: folderChildId });

            } else if (child.type == 'Project') {
              const projectChildId = typeof child.item === 'string'
                ? child.item
                : (child.item as IProject).projectInfo.id;

              if (projectChildId) tags.push({ type: 'Project', id: projectChildId });
            }
          });
        }
        return tags;
      },
      onQueryStarted: handleApiResult(),
    }),
    addFolder: builder.mutation<void, { data: Omit<IFolder, '_id' | 'path' | 'children' | 'createdAt' | 'updatedAt'>, showError?: boolean }>({
      query: ({ showError: _showError, data: folder }) => ({
        url: '/v1/folders',
        method: 'POST',
        body: folder,
      }),
      invalidatesTags: (_result, _error, { data: { parentFolder } }) => {
        const tags: { type: 'Folder'; id: string }[] = [{ type: 'Folder', id: 'LIST' }];

        if (parentFolder) {
          const parentId = typeof parentFolder === 'string' ? parentFolder : parentFolder._id;
          tags.push({ type: 'Folder', id: parentId });
        } else {
          tags.push({ type: 'Folder', id: 'root' });
        }

        return tags;
      },
      onQueryStarted: async (arg, api) => {
        if (arg.showError ?? true) return handleApiResult('Api_Folder_Add_Successful')(arg, api);
      },
    }),
    updateFolder: builder.mutation<void, Omit<IFolder, 'path' | 'children' | 'createdAt' | 'updatedAt'>>({
      query: ({ _id, ...rest }) => ({
        url: `/v1/folders/${_id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_result, _error, { _id, parentFolder }) => {
        const tags: ({ type: 'Folder'; id: string } | 'Project')[] = [
          { type: 'Folder', id: 'LIST' },
          { type: 'Folder', id: _id },
          'Project',
        ];

        if (parentFolder) {
          const parentId = typeof parentFolder === 'string' ? parentFolder : parentFolder._id;
          tags.push({ type: 'Folder', id: parentId });
        } else {
          tags.push({ type: 'Folder', id: 'root' });
        }

        return tags;
      },
      onQueryStarted: handleApiResult('Api_Folder_Update_Successful'),
    }),
    deleteFolder: builder.mutation<void, IFolder>({
      query: ({ _id }) => ({
        url: `/v1/folders/${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { _id, parentFolder }) => {
        const tags: ({ type: 'Folder'; id: string } | 'Project')[] = [
          { type: 'Folder', id: 'LIST' },
          { type: 'Folder', id: _id },
          'Project',
        ];

        if (parentFolder) {
          const parentId = typeof parentFolder === 'string' ? parentFolder : parentFolder._id;
          tags.push({ type: 'Folder', id: parentId });
        } else {
          tags.push({ type: 'Folder', id: 'root' });
        }

        return tags;
      },
      onQueryStarted: handleApiResult('Api_Folder_Delete_Successful'),
    }),
    addFolderShare: builder.mutation<void, { folderId: string; teamId: string; accessLevel: ShareAccessLevel }>({
      query: ({ folderId, ...body }) => ({
        url: `/v1/folders/${folderId}/shares`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { folderId }) => [
        { type: 'Folder', id: folderId },
        { type: 'Folder', id: 'LIST' },
        { type: 'Folder', id: 'root' },
        'Project',
        'TeamShare',
      ],
      onQueryStarted: handleApiResult('Api_Folder_Share_Add_Successful'),
    }),
    updateFolderShare: builder.mutation<void, { folderId: string; teamId: string; accessLevel: ShareAccessLevel }>({
      query: ({ folderId, teamId, accessLevel }) => ({
        url: `/v1/folders/${folderId}/shares/${teamId}`,
        method: 'PATCH',
        body: { accessLevel },
      }),
      invalidatesTags: (_result, _error, { folderId }) => [
        { type: 'Folder', id: folderId },
        { type: 'Folder', id: 'LIST' },
        { type: 'Folder', id: 'root' },
        'Project',
        'TeamShare',
      ],
      onQueryStarted: handleApiResult('Api_Folder_Share_Update_Successful'),
    }),
    removeFolderShare: builder.mutation<void, { folderId: string; teamId: string }>({
      query: ({ folderId, teamId }) => ({
        url: `/v1/folders/${folderId}/shares/${teamId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { folderId }) => [
        { type: 'Folder', id: folderId },
        { type: 'Folder', id: 'LIST' },
        { type: 'Folder', id: 'root' },
        'Project',
        'TeamShare',
      ],
      onQueryStarted: handleApiResult('Api_Folder_Share_Delete_Successful'),
    }),
  }),
});

export const {
  useGetFoldersQuery,
  useGetFolderQuery,
  useGetFolderByPathQuery,
  useLazyGetFolderByPathQuery,
  useAddFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useAddFolderShareMutation,
  useUpdateFolderShareMutation,
  useRemoveFolderShareMutation,
} = projectApiSlice;
